'use client';

// NOTE: The NPO Player is loaded on demand from the @npo/player package, see ./npoPlayer.ts

import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import axios from '@/lib/axios';
import { useEffect, useId, useRef, useState } from 'react';
import { mdiArrowRightCircle, mdiChevronDown } from '@mdi/js';
import Icon from '@mdi/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import useLocalStorage from '@/hooks/useLocalStorage';
import TextButton from '@/components/Button/TextButton';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import { ChatBlockProfilePicture } from '../ChatTextBlock/ChatTextBlock';

import styles from './NPOPlayerBlock.module.sass';
import { createNpoPlayer } from './npoPlayer';

interface NPOPlayerBlockData {
    prid: string;
    segment: {
        start: number | null;
        end: number | null;
    };
    transcript: string;
}

const NPOPlayerBlock = ({
    id,
    data,
    uuid,
    isEditable,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<NPOPlayerBlockData>({
        prid: data?.prid ?? '',
        segment: {
            start: data?.segment?.start ?? null,
            end: data?.segment?.end ?? null,
        },
        transcript:
            data?.transcript ??
            'Schrijf een gelijkwaardige beschrijving van de beelden en geluiden.',
    });

    if (isEditable) {
        return (
            <NPOPlayerBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    } else {
        return (
            <NPOPlayerViewer
                id={id}
                blockData={blockData}
                isAnimated={isAnimated}
                activeChatCharacter={activeChatCharacter}
                onContentHeightChanged={onContentHeightChanged}
                onFinished={onFinished}
            />
        );
    }
};

const NPOPlayerBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: NPOPlayerBlockData;
    setBlockData: (data: NPOPlayerBlockData) => void;
    onEdited?: BlockProps['onEdited'];
}) => {
    // Save changes of edits
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="bg-orange-200">
            <P
                isEditable
                rows={1}
                value={blockData.prid}
                onChange={(value) => {
                    setBlockData({
                        ...blockData,
                        prid: value,
                    });
                }}
                placeholder="Media ID"
            />
            {blockData.prid && (
                <div className="mt-4">
                    <P
                        isEditable
                        placeholder="Beeld- en geluidsbeschrijving (dingen, tekst, sprekers, geluiden, etc.)"
                        value={blockData.transcript}
                        onChange={(value) => {
                            setBlockData({
                                ...blockData,
                                transcript: value,
                            });
                        }}
                        maxLength={1024 * 4}
                        rows={12}
                    />
                    {/* <div className="mt-4 rounded-md bg-white p-4 ring-2 ring-slate-200">
                        <P className="text-md font-bold text-slate-400">
                            Segment (optioneel)
                        </P>
                        <div className="mt-2 grid grid-cols-2 gap-4">
                            <P
                                isEditable
                                value={`${blockData.segment.start ?? ''}`}
                                className={
                                    blockData.segment.start !== null
                                        ? ''
                                        : 'opacity-50'
                                }
                                rows={1}
                                onChange={(value) => {
                                    setBlockData({
                                        ...blockData,
                                        segment: {
                                            ...blockData.segment,
                                            start: value.length
                                                ? parseInt(value)
                                                : null,
                                        },
                                    });
                                }}
                                placeholder="Start (seconden)"
                            />
                            <P
                                isEditable
                                value={`${blockData.segment.end ?? ''}`}
                                className={
                                    blockData.segment.end !== null
                                        ? ''
                                        : 'opacity-50'
                                }
                                rows={1}
                                onChange={(value) => {
                                    setBlockData({
                                        ...blockData,
                                        segment: {
                                            ...blockData.segment,
                                            end: value.length
                                                ? parseInt(value)
                                                : null,
                                        },
                                    });
                                }}
                                placeholder="Einde (seconden)"
                            />
                        </div>
                    </div> */}
                </div>
            )}
        </BlockEditorContainer>
    );
};

const NPOPlayerViewer = ({
    id,
    blockData,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
}: {
    id: BlockProps['id'];
    blockData: NPOPlayerBlockData;
    isAnimated: BlockProps['isAnimated'];
    activeChatCharacter: BlockProps['activeChatCharacter'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
    onFinished: BlockProps['onFinished'];
}) => {
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const [hasFinished, setHasFinished] = useState<boolean>(false);
    const [isPlayerLoaded, setIsPlayerLoaded] = useState<boolean>(false);
    const [showPlayer, setShowPlayer] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isTranscriptShown, setIsTranscriptShown] = useState(false);

    const detailsId = useId();

    useEffect(() => {
        onContentHeightChanged?.();
    }, [showPlayer, isPlayerLoaded, isTranscriptShown]);

    // Load video
    useEffect(() => {
        const playerContainer = playerContainerRef.current;
        if (!playerContainer) return;

        let isMounted = true;
        let player: Awaited<ReturnType<typeof createNpoPlayer>> | null = null;

        axios
            .get(`block/${id}/npo-player-jwt?prid=${blockData.prid}`)
            .then(async (response) => {
                if (!isMounted || !response?.data?.jwt) return;

                const newPlayer = await createNpoPlayer({
                    container: playerContainer,
                    jwt: response.data.jwt,
                    segment: blockData.segment,
                    callbacks: {
                        onReady: () => {
                            setShowPlayer(true);
                            setIsPlayerLoaded(true);
                        },
                        onPlayingChanged: setIsPlaying,
                        onEnded: () => {
                            setHasFinished(true);
                            onFinished?.();
                        },
                    },
                });

                // The block was removed while the player was loading
                if (!isMounted) {
                    newPlayer.destroy().catch(() => {});
                    return;
                }
                player = newPlayer;
            })
            .catch((error) => {
                console.error('Failed to load NPO Player:', error);
                if (!isMounted) return;
                setShowPlayer(true);
                setIsPlayerLoaded(true);
            })
            .finally(() => {
                if (!isMounted) return;

                // If the prid contains spaces, show the player already
                // Editors have probably put in a note instead of a real prid
                // For testing it's easier to show the player so the chat can continue
                if (blockData.prid.includes(' ')) {
                    setShowPlayer(true);
                    setIsTranscriptShown(true);
                }
            });

        return () => {
            isMounted = false;
            player?.destroy().catch(() => {});
            playerContainer.innerHTML = '';
        };
    }, [id, blockData.prid]);

    return (
        <div className="relative">
            {!!activeChatCharacter && isAnimated && (
                <div className="absolute" aria-hidden>
                    <ChatBlockProfilePicture
                        className="absolute top-0 left-0"
                        name={activeChatCharacter.name}
                        isTyping={!showPlayer && !!isAnimated}
                    />
                </div>
            )}
            <section
                aria-label="Video"
                className={`${styles['npoplayerblock']} ${
                    isAnimated && styles['npoplayerblock--animated']
                } ${
                    !isPlayerLoaded && !showPlayer
                        ? styles['npoplayerblock--loading']
                        : ''
                } ${isPlaying ? styles['npoplayerblock--playing'] : ''}`}
            >
                {(!isPlayerLoaded || !showPlayer) && isAnimated && (
                    <div className="w-fit rounded-2xl rounded-tl-md bg-slate-200 p-2 font-bold">
                        <p className="flex flex-col px-2 text-slate-900">
                            <span className="animate-pulse text-lg">
                                verstuurt een video...
                            </span>
                        </p>
                    </div>
                )}
                <div className="relative rounded-b-lg transition-all overflow-hidden">
                    <section
                        aria-label="Video"
                        aria-details={detailsId}
                        className={`${styles['npoplayerblock__player']} ${
                            isPlayerLoaded || showPlayer
                                ? styles['npoplayerblock__player--loaded']
                                : ''
                        }`}
                    >
                        {/* The NPO Player adds its own classes to this element, so React must not manage its className */}
                        <div
                            ref={playerContainerRef}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </section>
                </div>
                <VideoTranscript
                    transcript={blockData.transcript}
                    detailsId={detailsId}
                    isTranscriptShown={isTranscriptShown}
                    setIsTranscriptShown={setIsTranscriptShown}
                    canFinish={!hasFinished && isAnimated}
                    onFinished={onFinished}
                />
            </section>
        </div>
    );
};

const VideoTranscript = ({
    transcript,
    detailsId,
    isTranscriptShown,
    setIsTranscriptShown,
    canFinish,
    onFinished,
}: {
    transcript: string;
    detailsId: string;
    isTranscriptShown: boolean;
    setIsTranscriptShown: (value: boolean) => void;
    canFinish?: boolean;
    onFinished?: () => void;
}) => {
    const reduceMotion = useReducedMotion();

    const [isIncreasedTextSizeEnabled] = useLocalStorage(
        'isIncreasedTextSizeEnabled',
        false,
    );

    return (
        <div
            id={detailsId}
            className={`
                ${styles['npoplayerblock__transcript']}
            `}
        >
            <button
                onClick={() => setIsTranscriptShown(!isTranscriptShown)}
                title={
                    !isTranscriptShown
                        ? 'Beeld- en geluidsbeschrijving tonen'
                        : 'Beeld- en geluidsbeschrijving verbergen'
                }
                aria-label="Beeld- en geluidsbeschrijving tonen"
                aria-expanded={isTranscriptShown}
                className={styles['npoplayerblock__transcript__button']}
            >
                <Icon
                    size={1}
                    path={mdiChevronDown}
                    className={`${
                        styles['npoplayerblock__transcript__button__icon']
                    } ${!isTranscriptShown ? '' : '-scale-y-100'}`}
                />
                Beeld- en geluidsbeschrijving
            </button>
            <AnimatePresence>
                {isTranscriptShown && (
                    <motion.div
                        initial={{
                            opacity: 0,
                            height: reduceMotion ? 'auto' : 0,
                        }}
                        animate={{ opacity: [0, 0, 1], height: 'auto' }}
                        exit={{
                            opacity: 0,
                            height: reduceMotion ? 'auto' : 0,
                            marginBottom: 0,
                        }}
                        className={`${
                            styles['npoplayerblock__transcript__text']
                        } ${isIncreasedTextSizeEnabled ? 'text-lg' : ''}`}
                    >
                        <p
                            className={`${styles['npoplayerblock__transcript__text__content']} select-text`}
                        >
                            {transcript.split('\n').map((line, index) => (
                                <span key={index} className="select-text">
                                    {line}
                                    <br />
                                </span>
                            ))}
                        </p>
                        {canFinish && !!onFinished && (
                            <div className="overflow-hidden pt-4 pb-8">
                                <TextButton
                                    className="mx-auto"
                                    icon={mdiArrowRightCircle}
                                    onClick={() => {
                                        onFinished();
                                        setIsTranscriptShown(false);
                                    }}
                                >
                                    Volgende
                                </TextButton>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NPOPlayerBlock;
